import { useState, useEffect } from 'react';
import { Flame, TrendingUp, TrendingDown, RefreshCw, X, BarChart2 } from 'lucide-react';
import { api } from '@/shared/api/axios';

export interface CrudeOilData {
  brent: {
    symbol: string;
    name: string;
    priceUsd: number;
    unit: string;
    changePercent: number;
  };
  wti: {
    symbol: string;
    name: string;
    priceUsd: number;
    unit: string;
    changePercent: number;
  };
  baseOil: {
    sn500: number;
    sn150: number;
    unit: string;
  };
  lastUpdated: string;
  source: string;
}

export function useCrudeOilPrice() {
  const [data, setData] = useState<CrudeOilData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPrice = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/forex/crude-oil');
      setData(res.data);
    } catch {
      // Fallback
      setData({
        brent: { symbol: 'BRENT', name: 'Brent Crude', priceUsd: 74.58, unit: 'USD/bbl', changePercent: 0.42 },
        wti: { symbol: 'WTI', name: 'WTI Crude', priceUsd: 70.82, unit: 'USD/bbl', changePercent: -0.28 },
        baseOil: { sn500: 945.0, sn150: 890.0, unit: 'USD/MT' },
        lastUpdated: new Date().toISOString(),
        source: 'Global Petroleum Benchmark',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrice();
    const interval = setInterval(fetchPrice, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return { data, loading, refetch: fetchPrice };
}

/** Topbar compact pill / button with modal */
export function CrudeOilTopbarPill() {
  const { data } = useCrudeOilPrice();
  const [showModal, setShowModal] = useState(false);

  if (!data) return null;

  const isUp = data.brent.changePercent >= 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        title="View Live Crude Oil & Base Oil Market Prices"
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100/90 text-amber-900 border border-amber-200/80 text-xs font-bold transition shadow-2xs cursor-pointer select-none"
      >
        <span className="flex h-2 w-2 relative">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
        </span>
        <span className="text-[11px] font-extrabold uppercase tracking-wide text-amber-800">Crude:</span>
        <span className="text-slate-900 font-bold">${data.brent.priceUsd}</span>
        <span className={`text-[10px] font-semibold flex items-center ${isUp ? 'text-emerald-700' : 'text-rose-600'}`}>
          {isUp ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
          {isUp ? `+${data.brent.changePercent}%` : `${data.brent.changePercent}%`}
        </span>
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-100 text-amber-700">
                  <Flame className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Petroleum & Crude Oil Benchmark</h3>
                  <p className="text-xs text-slate-500">Live commodity index for lubricants & raw materials</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-slate-500">Brent Crude Oil</span>
                  <div className="text-lg font-extrabold text-slate-900">${data.brent.priceUsd} <span className="text-xs font-medium text-slate-500">USD/barrel</span></div>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${data.brent.changePercent >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                  {data.brent.changePercent >= 0 ? `+${data.brent.changePercent}%` : `${data.brent.changePercent}%`}
                </span>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-slate-500">WTI Crude Oil</span>
                  <div className="text-lg font-extrabold text-slate-900">${data.wti.priceUsd} <span className="text-xs font-medium text-slate-500">USD/barrel</span></div>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${data.wti.changePercent >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                  {data.wti.changePercent >= 0 ? `+${data.wti.changePercent}%` : `${data.wti.changePercent}%`}
                </span>
              </div>

              <div className="p-3.5 rounded-xl border border-indigo-100 bg-indigo-50/50">
                <span className="text-xs font-bold text-indigo-900 flex items-center gap-1.5 mb-1.5">
                  <BarChart2 className="w-3.5 h-3.5 text-indigo-600" />
                  Base Oil Price Indicator (Import / Domestic Reference)
                </span>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white p-2 rounded-lg border border-indigo-100">
                    <span className="text-slate-500">SN 500:</span> <strong className="text-slate-900">${data.baseOil.sn500}</strong> / MT
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-indigo-100">
                    <span className="text-slate-500">SN 150:</span> <strong className="text-slate-900">${data.baseOil.sn150}</strong> / MT
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
              <span>Source: {data.source}</span>
              <span>Updated: {new Date(data.lastUpdated).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Full-width Dashboard Banner */
export function CrudeOilDashboardBanner() {
  const { data, loading, refetch } = useCrudeOilPrice();

  if (!data) return null;

  const isBrentUp = data.brent.changePercent >= 0;
  const isWtiUp = data.wti.changePercent >= 0;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-amber-200/90 bg-gradient-to-r from-amber-50 via-orange-50/40 to-slate-50 p-3.5 sm:p-4 shadow-xs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-sm">
            <Flame className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-extrabold uppercase tracking-wider text-amber-900">
                Live Petroleum & Crude Oil Market
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100/90 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                ACTIVE
              </span>
            </div>
            <p className="text-[11px] text-slate-600">
              Benchmark pricing for lubricant formulations, base oil import parity & cost forecasting.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs">
          {/* Brent */}
          <div className="flex items-center gap-2 rounded-xl border border-amber-200/80 bg-white px-3 py-1.5 shadow-2xs">
            <span className="font-semibold text-slate-500">Brent Crude:</span>
            <span className="font-extrabold text-slate-900">${data.brent.priceUsd}</span>
            <span className={`text-[10px] font-bold flex items-center ${isBrentUp ? 'text-emerald-700' : 'text-rose-600'}`}>
              {isBrentUp ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
              {isBrentUp ? `+${data.brent.changePercent}%` : `${data.brent.changePercent}%`}
            </span>
          </div>

          {/* WTI */}
          <div className="flex items-center gap-2 rounded-xl border border-amber-200/80 bg-white px-3 py-1.5 shadow-2xs">
            <span className="font-semibold text-slate-500">WTI Crude:</span>
            <span className="font-extrabold text-slate-900">${data.wti.priceUsd}</span>
            <span className={`text-[10px] font-bold flex items-center ${isWtiUp ? 'text-emerald-700' : 'text-rose-600'}`}>
              {isWtiUp ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
              {isWtiUp ? `+${data.wti.changePercent}%` : `${data.wti.changePercent}%`}
            </span>
          </div>

          {/* Base Oil SN 500 */}
          <div className="hidden lg:flex items-center gap-2 rounded-xl border border-indigo-200/70 bg-indigo-50/70 px-3 py-1.5 shadow-2xs">
            <span className="font-semibold text-indigo-800">Base Oil SN 500:</span>
            <span className="font-extrabold text-indigo-950">${data.baseOil.sn500}/MT</span>
          </div>

          <button
            onClick={() => refetch()}
            disabled={loading}
            title="Refresh Market Prices"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
}
